import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from './Sidebar';
import { BellIcon, UserIcon } from '@heroicons/react/24/outline';
import { useNotifications } from '../hooks/useNotifications';
import NotificationPanel from './NotificationPanel';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // Use the notifications hook
  const {
    notifications,
    unreadCount,
    loading: notificationsLoading,
    showNotifications,
    setShowNotifications,
    notificationPanelRef,
    handleMarkAllRead,
    handleNotificationClick,
    fetchingError,
    fetchNotifications,
  } = useNotifications();

  if (!user) {
    return <div className="min-h-screen">{children}</div>;
  }

  return (
    <div className="flex min-h-screen bg-gray-900 text-white">
      <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      
      <div className="flex-1 flex flex-col">
        {/* Compact Header - even more optimized for mobile */}
        <header className="bg-gray-800 shadow-lg px-2 py-1.5 sm:px-4 sm:py-2 z-30 w-full">
          <div className="flex justify-between items-center max-w-7xl mx-auto">
            <div className="flex items-center ml-12 md:ml-0"> {/* Add left margin for mobile to avoid overlap with hamburger */}
              <Link to="/dashboard" className="flex items-center space-x-2">
                <img
                  className="h-6 w-auto sm:h-7"
                  src="/logo.png"
                  alt="AutoLawn"
                />
                <span className="text-lg font-bold hidden sm:inline bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
                  AutoLawn
                </span>
              </Link>
              
              <Link 
                to="/dashboard" 
                className="ml-2 sm:ml-4 text-blue-100 px-1.5 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm font-medium rounded-md hover:bg-gray-700 hover:text-white transition-colors"
              >
                Dashboard
              </Link>
            </div>
            
            <div className="flex items-center space-x-1 sm:space-x-2">
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="text-blue-100 p-1 sm:p-2 rounded-full hover:bg-gray-700 hover:text-white transition-colors relative focus:outline-none"
                  aria-label="View Notifications"
                >
                  <BellIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                  {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 block h-3 w-3 sm:h-4 sm:w-4 rounded-full bg-red-500 text-white text-xs flex items-center justify-center transform translate-x-1 -translate-y-1">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
                
                {showNotifications && (
                  <NotificationPanel
                    notifications={notifications}
                    unreadCount={unreadCount}
                    loading={notificationsLoading}
                    fetchingError={fetchingError}
                    onMarkAllRead={handleMarkAllRead}
                    onNotificationClick={handleNotificationClick}
                    fetchNotifications={fetchNotifications}
                    panelRef={notificationPanelRef}
                  />
                )}
              </div>
              
              <Link 
                to="/profile" 
                className="text-blue-100 p-1 sm:p-2 rounded-full hover:bg-gray-700 hover:text-white transition-colors"
              >
                <UserIcon className="h-5 w-5 sm:h-6 sm:w-6" />
              </Link>
              
              <button
                onClick={logout}
                className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-2 py-1 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm font-medium hover:from-blue-600 hover:to-purple-600 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-blue-500/50"
              >
                Logout
              </button>
            </div>
          </div>
        </header>
        
        <main className="flex-1 overflow-y-auto bg-gray-900 pt-0">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout; 
